import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ShoppingBag } from "lucide-react";
import { ORDER_LINKS } from "../data/content";

const OrderContext = createContext(() => {});
export const useOrder = () => useContext(OrderContext);

const OrderModal = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          data-testid="order-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-md bg-background rounded-3xl overflow-hidden shadow-2xl"
            initial={{ scale: 0.95, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 16 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <div className="flex items-center gap-2.5">
                <span className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <ShoppingBag size={19} />
                </span>
                <div>
                  <h3 className="font-serif text-xl text-foreground leading-none">Order Online</h3>
                  <p className="text-xs text-foreground/55 mt-1">Choose how you'd like to order</p>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="order-modal-close"
                aria-label="Close"
                className="h-9 w-9 rounded-full flex items-center justify-center text-foreground/60 hover:bg-surface hover:text-foreground transition-colors"
              >
                <X size={19} />
              </button>
            </div>

            <div className="px-5 pb-6 space-y-3">
              {ORDER_LINKS.map((o) => (
                <a
                  key={o.platform}
                  href={o.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  data-testid={`order-option-${o.platform.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group flex items-center justify-between gap-4 rounded-2xl border px-5 py-4 transition-colors ${
                    o.recommended
                      ? "border-highlight bg-highlight/10 hover:bg-highlight/20"
                      : "border-surface bg-surface/40 hover:bg-surface"
                  }`}
                >
                  <div>
                    <p className="font-semibold text-foreground">{o.name}</p>
                    <p className="text-xs text-foreground/60 mt-0.5">{o.tag}</p>
                  </div>
                  <ArrowRight
                    size={18}
                    className="shrink-0 text-foreground/50 transition-transform group-hover:translate-x-1 group-hover:text-foreground"
                  />
                </a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const OrderProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openOrder = useCallback(() => setOpen(true), []);
  return (
    <OrderContext.Provider value={openOrder}>
      {children}
      <OrderModal open={open} onClose={() => setOpen(false)} />
    </OrderContext.Provider>
  );
};
